import { IconButton } from '@mui/material';
import {
	memo,
	startTransition,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Control, useWatch } from 'react-hook-form';
import { FaTrash } from 'react-icons/fa';
import { useDebounce } from 'use-debounce';
import SearchSelect from '@/components/atoms/SearchSelect';
import { useAllEmployees, useEmployeeById } from '../hooks/useApprovalFlowData';
import { ApprovalFlowFormData } from '../types';

interface ApproverItemProps {
	stepIndex: number;
	approverIndex: number;
	control: Control<ApprovalFlowFormData>;
	onRemove: () => void;
}

export const ApproverItem = memo(
	({ stepIndex, approverIndex, control, onRemove }: ApproverItemProps) => {
		const [employeeSearch, setEmployeeSearch] = useState('');
		const [debouncedEmployeeSearch] = useDebounce(employeeSearch, 500);
		const [options, setOptions] = useState<{ value: number; label: string }[]>(
			[],
		);
		const selectedValueRef = useRef<number | null>(null);
		const selectedEmployeeRef = useRef<{ value: number; label: string } | null>(
			null,
		);
		const hasInitialLoadRef = useRef(false);

		const fieldName = `steps.${stepIndex}.approvers.${approverIndex}` as const;
		const selectedValue = useWatch({ control, name: fieldName });

		const isValidSelectedValue = !!(
			selectedValue &&
			typeof selectedValue === 'number' &&
			selectedValue > 0
		);

		const { data: allEmployeeOptions, isLoading: isLoadingEmployees } =
			useAllEmployees(debouncedEmployeeSearch);

		// biome-ignore lint/correctness/useExhaustiveDependencies: isValidSelectedValue is derived from selectedValue, which is already in the dependencies, so it's not necessary to include it
		const selectedValueInOptions = useMemo(() => {
			if (!isValidSelectedValue) return true;
			return (
				allEmployeeOptions?.some(
					(opt: { value: number; label: string }) =>
						opt.value === selectedValue,
				) || false
			);
		}, [allEmployeeOptions, selectedValue]);

		const shouldFetchEmployee =
			isValidSelectedValue &&
			!selectedValueInOptions &&
			selectedValueRef.current !== selectedValue;

		const { data: selectedEmployeeData } = useEmployeeById(
			selectedValue,
			shouldFetchEmployee,
		);

		// biome-ignore lint/correctness/useExhaustiveDependencies: isValidSelectedValue is derived from selectedValue, which is already in the dependencies, so it's not necessary to include it
		useEffect(() => {
			if (isValidSelectedValue) {
				selectedValueRef.current = selectedValue;
			}
		}, [selectedValue]);

		useEffect(() => {
			if (selectedEmployeeData && selectedValueRef.current === selectedValue) {
				selectedEmployeeRef.current = selectedEmployeeData;
			}
		}, [selectedEmployeeData, selectedValue]);

		useEffect(() => {
			startTransition(() => {
				if (allEmployeeOptions && allEmployeeOptions.length > 0) {
					hasInitialLoadRef.current = true;
					let finalOptions = [...allEmployeeOptions];

					if (
						selectedValueRef.current &&
						!finalOptions.find((opt) => opt.value === selectedValueRef.current)
					) {
						const selectedOption =
							selectedEmployeeRef.current || selectedEmployeeData;
						if (selectedOption) {
							finalOptions = [selectedOption, ...finalOptions];
						}
					}

					setOptions(finalOptions);
				} else if (
					selectedValueRef.current &&
					(selectedEmployeeRef.current || selectedEmployeeData)
				) {
					const selectedOption =
						selectedEmployeeRef.current || selectedEmployeeData;
					if (selectedOption) {
						setOptions([selectedOption]);
					}
				}
			});
		}, [allEmployeeOptions, selectedEmployeeData]);

		const handleSearchChange = useCallback((value: string) => {
			setEmployeeSearch(value);
		}, []);

		const isInitialLoading =
			!hasInitialLoadRef.current &&
			isLoadingEmployees &&
			isValidSelectedValue &&
			options.length === 0;

		return (
			<div className="flex justify-between items-end gap-2">
				<SearchSelect
					title="Colaborador"
					options={options}
					control={control}
					name={fieldName}
					setSearch={handleSearchChange}
					disabled={isInitialLoading}
					containerClassName="flex-1"
					width="100%"
					required
				/>
				<IconButton onClick={onRemove}>
					<FaTrash size={16} />
				</IconButton>
			</div>
		);
	},
);

ApproverItem.displayName = 'ApproverItem';
